import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  omitUntouchedMeasurements,
  useMeasurementUnit,
  useMeasurementUnitReady,
  useRefillOnUnitSwitch,
} from 'hooks/useMeasurementUnit';
import { withMeasurementUnitScope } from 'hooks/useMeasurementUnitScope';
import { RevxUnitSelect } from 'components/RevxUnitSelect';
import { inViewerUnit } from 'utils/measurement';
import {
  Affix,
  Divider,
  Form,
  Input,
  PageHeader,
  Popconfirm,
  Switch,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ArrowLeftIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { Headline } from 'components/RevxTypography';
import { isPlatformOrg } from 'domains/orgDomain';
import { useOrg } from 'hooks/useOrg';
import { isUndefined } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { MeasureType } from 'domains/measurementDomain';
import { RevxMeasureTextInput } from 'components/RevxMeasureInput/RevxMeasureTextInput';
import { ADMIN_DETAIL_AFFIX_TOP } from 'utils/constants';
import {
  RULE_INTEGER,
  RULE_INTEGER_LIST,
  RULE_MEASURE_PRECISION,
  RULE_PRECISION,
  RULE_TEXT_FIXED_LEN,
  RULE_TEXT_LEN_255,
} from 'utils/validators';
import { useState } from 'react';
import { useMount } from 'ahooks';

const MatItemForm = () => {
  const [displayPaperDisabled, setDisplayPaperDisabled] = useState(false);
  const { t } = useTranslation();
  const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 10 },
  };

  const [form] = Form.useForm();

  const { unit, tag } = useMeasurementUnit();
  // Held until the org's system is known - see useMeasurementUnitReady. Opening this form in the
  // gap would label every measurement field with a unit that is about to change.
  const unitReady = useMeasurementUnitReady();
  const dispatch = useAppDispatch();
  const saving = useAppSelector((state) => state.loading.effects.mat.saveItem);
  const item = useAppSelector((state) => state.mat.item);
  const org = useOrg();
  const base = org && isPlatformOrg(org);
  const hasBaseRef = !isUndefined(item) && Boolean(item.baseRefId);

  useMount(() => {
    publicAvailableChange();
  });

  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }

    const updated = tag(
      omitUntouchedMeasurements(
        Object.assign({}, item, values, {
          skuTypeFilter: (values.skuTypeFilterStr as string)
            .split('|')
            .map((s) => parseInt(s)),
        }),
        form
      )
    );
    await dispatch.mat.saveItem(updated);
    onClose();
  };
  const onClose = () => {
    dispatch.mat.setState({ item: undefined });
  };
  const onSaveAsNew = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }
    const updated = tag(
      omitUntouchedMeasurements(
        Object.assign({}, item, values, {
          skuTypeFilter: (values.skuTypeFilterStr as string)
            .split('|')
            .map((s) => parseInt(s)),
          // Cleared here rather than after the omit: this is a create, and
          // `omitUntouchedMeasurements` keys off `id` to know there is nothing stored to leave
          // alone. Cleared afterwards, every dimension nobody retyped was dropped first.
          id: undefined,
        }),
        form
      )
    );
    await dispatch.mat.saveItem(updated);
    onClose();
  };

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.mat.removeItem(item.id);
      onClose();
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {item?.id
                ? t('platform.mat.form.title.edit')
                : t('platform.mat.form.title.create')}
            </Headline>
          }
          subTitle={<RevxUnitSelect />}
          extra={[
            <RevxButton
              type="primary"
              htmlType="submit"
              brandGreen
              onClick={onSave}
            >
              {t('common.save')}
            </RevxButton>,
            ...(item?.id
              ? [
                  <RevxButton onClick={onSaveAsNew} type="primary" brandGreen>
                    {t('common.saveAsNew')}
                  </RevxButton>,
                ]
              : []),
            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen>
                {t('common.cancel')}
              </RevxButton>
            </>,
            ...(item?.id
              ? [
                  <>
                    <Divider type="vertical" />
                    <Popconfirm
                      title={t('common.confirmQuestion')}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      onConfirm={onRemove}
                      okText={t('common.ok')}
                      cancelText={t('common.cancel')}
                      overlayStyle={{ width: 160 }}
                      getPopupContainer={(e) => {
                        return e as HTMLElement;
                      }}
                    >
                      <RevxButton danger>{t('common.remove')}</RevxButton>
                    </Popconfirm>
                  </>,
                ]
              : []),
          ]}
        />
      </Affix>
    </div>
  );

  const publicAvailableChange = () => {
    const isPublic = form.getFieldValue('isPublic');
    if (!isPublic) {
      form.setFieldsValue({ displayInMyPaper: true });
      setDisplayPaperDisabled(true);
    }
    setDisplayPaperDisabled(!isPublic);
  };

  useRefillOnUnitSwitch(form, item);

  return (
    <RevxSpin spinning={saving || !unitReady}>
      {actionBar}
      <div className="platform-item-form-wrapper">
        <Form
          className="platform-item-form"
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...inViewerUnit(item, unit),
            skuTypeFilterStr: item?.skuTypeFilter
              ?.map((t) => (t || 0).toString())
              .join('|'),
          }}
        >
          <Form.Item
            label={t('common.active')}
            name="active"
            valuePropName="checked"
          >
            <Switch disabled={!Boolean(item?.id)} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.isPublic')}
            name="isPublic"
            valuePropName="checked"
          >
            <Switch disabled={base} onChange={publicAvailableChange} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.displayInMyPaper')}
            name="displayInMyPaper"
            valuePropName="checked"
          >
            <Switch disabled={displayPaperDisabled} />
          </Form.Item>

          <Form.Item
            label={t('common.name')}
            name="name"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('common.description')}
            name="description"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input.TextArea rows={3} disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.table.columns.category')}
            name="category"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.table.columns.basisWeight')}
            name="basisWeight"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item label={t('common.size')}>
            <Input.Group compact>
              <Form.Item
                label={t('common.width')}
                noStyle
                name="widthIn"
                rules={[RULE_MEASURE_PRECISION(3, MeasureType.LENGTH, form)]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  style={{ width: '45%' }}
                  addonBefore={t('common.width')}
                  disabled={hasBaseRef}
                />
              </Form.Item>
              <Form.Item
                label={t('common.length')}
                noStyle
                name="lengthIn"
                rules={[RULE_MEASURE_PRECISION(3, MeasureType.LENGTH, form)]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  style={{ width: '45%', marginLeft: '1rem' }}
                  addonBefore={t('common.length')}
                  disabled={hasBaseRef}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.mat.table.columns.color')}
            name="color"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.table.columns.finish')}
            name="finish"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.filter1')}
            name="filter1"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.filter2')}
            name="filter2"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.constraint1')}
            name="constraint1"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.constraint2')}
            name="constraint2"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.skuTypeFilter')}
            name="skuTypeFilterStr"
            rules={[RULE_INTEGER_LIST()]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item label={t('platform.mat.form.itemLabels.uom')}>
            <Input.Group compact>
              <Form.Item
                label={t('platform.mat.form.itemLabels.base')}
                noStyle
                name="baseUom"
                rules={[RULE_TEXT_FIXED_LEN(2)]}
              >
                <Input
                  style={{ width: '45%' }}
                  addonBefore={t('platform.mat.form.itemLabels.base')}
                  disabled={hasBaseRef}
                />
              </Form.Item>
              <Form.Item
                label={t('platform.mat.form.itemLabels.price')}
                noStyle
                name="priceUom"
                rules={[RULE_TEXT_FIXED_LEN(2)]}
              >
                <Input
                  style={{ width: '45%', marginLeft: '1rem' }}
                  addonBefore={t('platform.mat.form.itemLabels.price')}
                  disabled={hasBaseRef}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item label={t('platform.mat.form.itemLabels.unitCost')}>
            <Input.Group compact>
              <Form.Item
                label={t('platform.mat.form.itemLabels.unitCost')}
                noStyle
                name="unitCostUsd"
                rules={[RULE_PRECISION(7)]}
              >
                <Input style={{ width: '45%' }} prefix="$" />
              </Form.Item>
              <Form.Item
                label={t('common.type')}
                noStyle
                name="costUnitType"
                rules={[RULE_TEXT_FIXED_LEN(2)]}
              >
                <Input
                  style={{ width: '45%', marginLeft: '1rem' }}
                  addonBefore={t('common.type')}
                  disabled={hasBaseRef}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.price')}
            name="priceUsd"
            rules={[RULE_PRECISION(6)]}
          >
            <Input prefix="$" />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.salesTax')}
            name="salesTaxUsd"
            rules={[RULE_PRECISION(2)]}
          >
            <Input prefix="$" />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.markup')}
            name="markupPct"
            rules={[RULE_INTEGER]}
          >
            <Input prefix="%" />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.vendorId')}
            name="vendorId"
            rules={[RULE_INTEGER]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.vendorSku')}
            name="vendorSku"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.table.columns.manufacturer')}
            name="manufacturerName"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.manufacturerSku')}
            name="manufacturerSku"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.table.columns.stockType')}
            name="stockType"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.pcwRecycledPct')}
            name="pcwRecycledPct"
            rules={[RULE_INTEGER]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.greensealCert')}
            name="greensealCert"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.fscCert')}
            name="fscCert"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.sfiCert')}
            name="sfiCert"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item label={t('platform.mat.form.itemLabels.diameter')}>
            <Input.Group compact>
              <Form.Item
                label={t('platform.mat.form.itemLabels.outside')}
                noStyle
                name="outsideDiameterIn"
                rules={[RULE_MEASURE_PRECISION(2, MeasureType.LENGTH, form)]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  style={{ width: '45%' }}
                  addonBefore={t('platform.mat.form.itemLabels.outside')}
                  disabled={hasBaseRef}
                />
              </Form.Item>
              <Form.Item
                label={t('platform.mat.form.itemLabels.core')}
                noStyle
                name="coreDiameterIn"
                rules={[RULE_MEASURE_PRECISION(2, MeasureType.LENGTH, form)]}
              >
                <RevxMeasureTextInput
                  type={MeasureType.LENGTH}
                  style={{ width: '45%', marginLeft: '1rem' }}
                  addonBefore={t('platform.mat.form.itemLabels.core')}
                  disabled={hasBaseRef}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.mat.table.columns.caliper')}
            name="caliperIn"
            rules={[RULE_MEASURE_PRECISION(5, MeasureType.CALIPER, form)]}
          >
            <RevxMeasureTextInput type={MeasureType.CALIPER} disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item label={t('platform.mat.form.itemLabels.package')}>
            <Input.Group compact>
              <Form.Item
                label={t('common.quantityAbbrev')}
                noStyle
                name="packageQty"
                rules={[RULE_INTEGER]}
              >
                <Input
                  style={{ width: '45%' }}
                  addonBefore={t('common.quantityAbbrev')}
                  disabled={hasBaseRef}
                />
              </Form.Item>
              <Form.Item
                label={t('common.unit')}
                noStyle
                name="packageUnit"
                rules={[RULE_TEXT_LEN_255]}
              >
                <Input
                  style={{ width: '45%', marginLeft: '1rem' }}
                  addonBefore={t('common.unit')}
                  disabled={hasBaseRef}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.weight')}
            name="weightLb"
            rules={[RULE_MEASURE_PRECISION(4, MeasureType.WEIGHT, form)]}
          >
            <RevxMeasureTextInput type={MeasureType.WEIGHT} disabled={hasBaseRef} />
          </Form.Item>
          <Form.Item
            label={t('platform.mat.form.itemLabels.mweight')}
            name="mweightLb"
            rules={[RULE_MEASURE_PRECISION(2, MeasureType.WEIGHT, form)]}
          >
            <RevxMeasureTextInput type={MeasureType.WEIGHT} disabled={hasBaseRef} />
          </Form.Item>

          <Form.Item
            label={t('platform.mat.form.itemLabels.volume')}
            name="volumeCubicFt"
            rules={[RULE_MEASURE_PRECISION(6, MeasureType.VOLUME, form)]}
          >
            <RevxMeasureTextInput type={MeasureType.VOLUME} disabled={hasBaseRef} />
          </Form.Item>
        </Form>
      </div>
    </RevxSpin>
  );
};
export default withMeasurementUnitScope(MatItemForm);
